// /app/frontend/src/pages/AdminDashboard.jsx
//
// Admin Panel — Dashboard.
//
// Deliberately minimal:
//   • 4 stat cards: Total, Today, Week, Month
//   • Recent users table (last 20)
// Backend `require_admin` gates every request — the UI is a thin skin.
//
// Kept modular so we can add sub-routes later (e.g. /admin/users,
// /admin/moderation) without touching this file's data-fetching or
// design system.

import React, { useCallback, useEffect, useState } from "react";
import { Users, UserPlus, CalendarDays, TrendingUp, RefreshCw, Loader2, ShieldCheck, Crown } from "lucide-react";
import { fetchAdminStats, fetchAdminRecentUsers } from "../lib/adminApi";
import AdminUsersSection from "../components/AdminUsersSection";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
}

const initial = (s) => (s?.trim?.()?.[0] || "?").toUpperCase();

function StatCard({ label, value, icon: Icon, accent = "emerald", testId }) {
  const accentBg = {
    emerald: "bg-emerald-500/10 text-emerald-600",
    sky: "bg-sky-500/10 text-sky-600",
    violet: "bg-violet-500/10 text-violet-600",
    amber: "bg-amber-500/10 text-amber-600",
  }[accent];
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900"
    >
      <div className="flex items-center justify-between">
        <div className={`grid h-11 w-11 place-items-center rounded-xl ${accentBg}`}>
          <Icon size={20} />
        </div>
      </div>
      <div className="mt-4 text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
        {typeof value === "number" ? value.toLocaleString() : "—"}
      </div>
      <div className="mt-1 text-sm text-zinc-500">{label}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const [s, u] = await Promise.all([
        fetchAdminStats(),
        fetchAdminRecentUsers({ limit: 20 }),
      ]);
      setStats(s);
      setUsers(u);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load admin data.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-emerald-600" />
            <h1 className="font-heading text-2xl font-semibold tracking-tight">Admin Panel</h1>
          </div>
          <p className="mt-0.5 text-sm text-zinc-500">
            Live snapshot of Syllox growth. All numbers come straight from the database.
          </p>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
          data-testid="admin-refresh-btn"
        >
          {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-300" data-testid="admin-error">
          {error}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total Users"
          value={loading ? null : stats?.total_users ?? 0}
          icon={Users}
          accent="emerald"
          testId="admin-stat-total"
        />
        <StatCard
          label="New Today"
          value={loading ? null : stats?.new_today ?? 0}
          icon={UserPlus}
          accent="sky"
          testId="admin-stat-today"
        />
        <StatCard
          label="New This Week"
          value={loading ? null : stats?.new_this_week ?? 0}
          icon={CalendarDays}
          accent="violet"
          testId="admin-stat-week"
        />
        <StatCard
          label="New This Month"
          value={loading ? null : stats?.new_this_month ?? 0}
          icon={TrendingUp}
          accent="amber"
          testId="admin-stat-month"
        />
      </div>

      {/* Recent users */}
      <div
        className="rounded-2xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900"
        data-testid="admin-recent-users-card"
      >
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4 dark:border-zinc-800">
          <div>
            <h2 className="font-heading text-lg font-semibold">Recent Users</h2>
            <p className="mt-0.5 text-xs text-zinc-500">Newest {users.length} sign-ups.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-zinc-500">
            <Loader2 size={14} className="animate-spin" /> Loading users…
          </div>
        ) : users.length === 0 ? (
          <div className="py-10 text-center text-sm text-zinc-500">No users yet.</div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full text-sm">
                <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500 dark:bg-zinc-900/50">
                  <tr>
                    <th className="px-5 py-2.5 font-medium">User</th>
                    <th className="px-5 py-2.5 font-medium">Email</th>
                    <th className="px-5 py-2.5 font-medium">Provider</th>
                    <th className="px-5 py-2.5 font-medium">Premium</th>
                    <th className="px-5 py-2.5 font-medium">Joined</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                  {users.map((u) => (
                    <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`} className="hover:bg-zinc-50 dark:hover:bg-zinc-900/50">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          {u.picture ? (
                            <img src={u.picture} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
                          ) : (
                            <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-emerald-500 text-xs font-semibold text-white">
                              {initial(u.name || u.username)}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="truncate font-medium text-zinc-900 dark:text-zinc-100">{u.name || "—"}</div>
                            <div className="truncate text-xs text-zinc-500">@{u.username || "—"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-zinc-700 dark:text-zinc-300">{u.email || "—"}</td>
                      <td className="px-5 py-3">
                        <span className="rounded-full bg-zinc-500/10 px-2 py-0.5 text-xs capitalize text-zinc-600 dark:text-zinc-400">
                          {u.auth_provider || "email"}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        {u.is_premium ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300">
                            <Crown size={12} /> Premium
                          </span>
                        ) : (
                          <span className="text-xs text-zinc-400">Free</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-xs text-zinc-500">{fmtDate(u.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <div className="divide-y divide-zinc-100 md:hidden dark:divide-zinc-800">
              {users.map((u) => (
                <div key={u.user_id} className="flex items-start gap-3 px-5 py-3" data-testid={`admin-user-card-${u.user_id}`}>
                  {u.picture ? (
                    <img src={u.picture} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
                  ) : (
                    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-emerald-500 text-xs font-semibold text-white">
                      {initial(u.name || u.username)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="truncate font-medium">{u.name || "—"}</div>
                      {u.is_premium && (
                        <Crown size={12} className="shrink-0 text-amber-500" aria-label="premium" />
                      )}
                    </div>
                    <div className="truncate text-xs text-zinc-500">{u.email || "—"}</div>
                    <div className="mt-1 text-[11px] text-zinc-400">
                      {u.auth_provider || "email"} · joined {fmtDate(u.created_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* All accounts — searchable + paginated + admin-delete */}
      <AdminUsersSection onUserDeleted={load} />
    </div>
  );
}
